import { Stack, Text, textVariants, vars } from '@etg/wings';
import { useDateFormat, useSiteContext, useTranslation } from '@eti/providers';
import { CalendarBlank } from '@phosphor-icons/react';
import { useMemo } from 'react';
import { date as dateUtil, duration as durationUtil } from '@eti/utils';
import { css } from '@eti/styles';
import { getBoundDate } from '../../../../../../widgets/trip-details/utils/format';
import TimelineWrapper from '../../../common/TimelineWrapper';
import TimelineRow from '../common/TimelineRow';
import { getTotalTravelDuration } from '../common/utils/utils';
import { useDurationHTML } from '../../../../../../common/time/utils/useDurationHTML';
import type { TransformedSegment } from '../../../../../../widgets/trip-itinerary/models';
import Time from '../../../../../../common/time/Time';

const latestDepartureDateStyles = (isDateChanged: boolean) => css`
  background-color: ${isDateChanged ? vars.colors.signal.caution.background : 'none'};
`;

const originalDepartureDateStyles = css`
  color: #737373;
  text-decoration: line-through;
`;

interface ItineraryBoundHeaderProps {
  originalDepartureDate: string | null;
  segments: TransformedSegment[];
}

const ItineraryBoundHeader = ({ originalDepartureDate, segments }: ItineraryBoundHeaderProps) => {
  const { t } = useTranslation();
  const { weekdayYear } = useDateFormat();
  const { language, timeFormat } = useSiteContext();
  const { getDurationHTML } = useDurationHTML();
  const calendarDate = getBoundDate(segments, weekdayYear);
  const originalCalendarDate = originalDepartureDate
    ? dateUtil.format(originalDepartureDate, weekdayYear)
    : null;
  const totalTravelDuration = useMemo(() => getTotalTravelDuration(segments), [segments]);
  const localizedTravelDuration = useMemo(
    () => durationUtil.getDuration(totalTravelDuration, language.code, timeFormat),
    [language.code, timeFormat, totalTravelDuration],
  );
  const isDateChanged = originalCalendarDate !== null && calendarDate !== originalCalendarDate;

  return (
    <div data-testid="itinerary-bound-header">
      {Boolean(totalTravelDuration) && (
        <Text as="p" asHtml data-testid="itinerary-bound-travel-time">
          {t(
            'TripDetails.Itinerary.TravelTime',
            getDurationHTML(
              localizedTravelDuration,
              totalTravelDuration,
              'itinerary-bound-travel-time-duration',
            ),
          )}
        </Text>
      )}
      <TimelineRow>
        <TimelineWrapper borderColorAfter={vars.colors.inputs.border} borderStyleAfter="dashed">
          <CalendarBlank
            aria-hidden
            data-testid="itinerary-bound-calendar"
            size={21}
            weight="regular"
          />
        </TimelineWrapper>
        <Stack align="start" spacing={0}>
          <Text
            as={Time}
            className={latestDepartureDateStyles(isDateChanged)}
            data-testid="itinerary-bound-departure-date"
            date={Number.parseInt(getBoundDate(segments, 'T'), 10)}
            format="date"
            screenReaderTextKey={
              isDateChanged
                ? 'ScreenReader.TripDetails.Details.NewDepartureDate'
                : 'ScreenReader.TripDetails.Details.DepartureDate'
            }
            variant={textVariants._16_BOLD}
          >
            {calendarDate}
          </Text>
          {originalDepartureDate && isDateChanged && (
            <Text
              as={Time}
              className={originalDepartureDateStyles}
              data-testid="itinerary-bound-original-departure-date"
              date={originalDepartureDate}
              format="date"
              screenReaderTextKey="ScreenReader.TripDetails.Details.OriginalDepartureDate"
              variant={textVariants._16_BOLD}
            >
              {originalCalendarDate}
            </Text>
          )}
        </Stack>
      </TimelineRow>
    </div>
  );
};

export default ItineraryBoundHeader;
